import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { motion } from 'framer-motion';
import { cn } from './cn';

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  cell: (row: T, index: number) => ReactNode;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  /** 헤더만 다른 정렬(예: 셀은 왼쪽, 헤더는 가운데). 없으면 align 을 따른다. */
  headerAlign?: 'left' | 'center' | 'right';
  /**
   * 이 컬럼 헤더를 클릭 정렬 가능하게 한다(오름/내림 토글). 켜면 헤더가 버튼이 되고
   * 활성 정렬 시 방향 표시(↑/↓)+aria-sort 가 붙는다. 실제 정렬 동작은 DataTableProps.sort
   * /onSortChange 로 상위에서 제어한다(서버 정렬이든 클라 정렬이든 무관).
   */
  sortable?: boolean;
}

export type DataTableSortDirection = 'asc' | 'desc';

export interface DataTableSort {
  key: string;
  direction: DataTableSortDirection;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string | number;
  selectable?: boolean;
  selectedKeys?: Set<string | number>;
  onSelectionChange?: (keys: Set<string | number>) => void;
  expandable?: boolean;
  renderExpanded?: (row: T) => ReactNode;
  emptyState?: ReactNode;
  className?: string;
  stickyHeader?: boolean;
  /** Dense row baseline — defaults to true (compact 32px row, 카페24 정합). */
  dense?: boolean;
  /** Zebra striping using `--table-zebra-bg` token (default false). */
  zebra?: boolean;
  /** Collapse to card list below tablet breakpoint (< 991px). */
  mobileCardCollapse?: boolean;
  /** ARIA label for table (a11y). */
  ariaLabel?: string;
  /**
   * 현재 활성 정렬(컬럼 key + 방향). null = 정렬 없음. sortable 컬럼과 함께 쓴다.
   * 정렬 상태/적용은 상위가 소유(제어 컴포넌트) — DataTable 은 표시·클릭 콜백만 담당.
   */
  sort?: DataTableSort | null;
  /**
   * sortable 헤더 클릭 시 호출(인자 = 컬럼 key). 상위에서 같은 key 면 방향 토글, 다른 key 면
   * 새 컬럼 정렬로 전환하도록 구현한다(toggleDataTableSort 헬퍼 제공).
   */
  onSortChange?: (key: string) => void;
}

/**
 * 정렬 토글 헬퍼 — 상위 상태에서 onSortChange(key) 처리 시 사용. 같은 컬럼이면 asc↔desc 토글,
 * 다른 컬럼이면 그 컬럼 asc 로 시작. 불변(새 객체 반환).
 */
export const toggleDataTableSort = (
  current: DataTableSort | null | undefined,
  key: string,
): DataTableSort =>
  current && current.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: 'asc' };

const STAGGER_LIMIT = 10;
const EASE = [0.16, 1, 0.3, 1] as [number, number, number, number];

const buildThStyle = (dense: boolean): CSSProperties => ({
  padding: dense ? '0 var(--table-cell-px)' : '0 20px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0,
  color: 'var(--content-muted)',
  background: 'var(--surface-1)',
  borderBottom: '1px solid var(--divider-strong)',
  whiteSpace: 'nowrap',
  height: dense ? 'var(--table-head-h)' : 48,
});

const buildTdStyle = (dense: boolean): CSSProperties => ({
  padding: dense
    ? 'var(--table-cell-py) var(--table-cell-px)'
    : '14px 20px',
  fontSize: dense ? 13 : 14,
  color: 'var(--content-default)',
  borderBottom: '1px solid var(--divider-soft)',
  verticalAlign: 'middle',
});

interface RowProps<T> {
  row: T;
  index: number;
  rowKeyValue: string | number;
  columns: DataTableColumn<T>[];
  selectable: boolean;
  expandable: boolean;
  isSelected: boolean;
  isExpanded: boolean;
  dense: boolean;
  zebra: boolean;
  tdStyle: CSSProperties;
  onToggleRow: (key: string | number) => void;
  onToggleExpand: (key: string | number) => void;
}

function DataTableRowImpl<T>({
  row,
  index,
  rowKeyValue,
  columns,
  selectable,
  expandable,
  isSelected,
  isExpanded,
  dense,
  zebra,
  tdStyle,
  onToggleRow,
  onToggleExpand,
}: RowProps<T>) {
  const zebraBg = zebra && index % 2 === 1 ? 'var(--table-zebra-bg)' : 'var(--surface-raised)';
  const rowBgColor = isSelected ? 'var(--badge-info-bg)' : zebraBg;
  const shouldAnimate = index < STAGGER_LIMIT;

  const leadingCells: ReactNode[] = [
    selectable ? (
      <td key="__select" style={tdStyle}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleRow(rowKeyValue)}
          aria-label="행 선택"
        />
      </td>
    ) : null,
    expandable ? (
      <td key="__expand" style={tdStyle}>
        <button
          type="button"
          onClick={() => onToggleExpand(rowKeyValue)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 2,
            color: 'var(--content-muted)',
            fontSize: 12,
          }}
          aria-label={isExpanded ? '접기' : '펼치기'}
          aria-expanded={isExpanded}
        >
          {isExpanded ? '▲' : '▼'}
        </button>
      </td>
    ) : null,
  ];

  const cells = (
    <>
      {leadingCells}
      {columns.map((col) => (
        <td key={col.key} style={{ ...tdStyle, textAlign: col.align ?? 'left' }}>
          {col.cell(row, index)}
        </td>
      ))}
    </>
  );

  const commonProps = {
    style: { background: rowBgColor },
    whileHover: { backgroundColor: 'var(--surface-1)' },
    'aria-selected': isSelected || undefined,
  } as const;

  if (shouldAnimate) {
    return (
      <motion.tr
        {...commonProps}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: dense ? 0.14 : 0.18, delay: index * 0.02, ease: EASE }}
      >
        {cells}
      </motion.tr>
    );
  }

  return <motion.tr {...commonProps}>{cells}</motion.tr>;
}

const DataTableRow = memo(DataTableRowImpl) as <T>(props: RowProps<T>) => ReturnType<typeof DataTableRowImpl<T>>;

function useViewportBelow(maxWidthPx: number): boolean {
  const getMatches = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${maxWidthPx - 1}px)`).matches;
  }, [maxWidthPx]);

  const [below, setBelow] = useState<boolean>(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width: ${maxWidthPx - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setBelow(e.matches);
    setBelow(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [maxWidthPx]);

  return below;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  selectable,
  selectedKeys,
  onSelectionChange,
  expandable,
  renderExpanded,
  emptyState,
  className,
  stickyHeader,
  dense = true,
  zebra = false,
  mobileCardCollapse = false,
  ariaLabel,
  sort,
  onSortChange,
}: DataTableProps<T>) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string | number>>(new Set());

  const thStyle = useMemo(() => buildThStyle(dense), [dense]);
  const tdStyle = useMemo(() => buildTdStyle(dense), [dense]);

  // rowKey 가 데이터 결함으로 중복 값을 내면 두 행이 같은 selection/React key 를
  // 공유해 "한 행만 클릭해도 두 행이 체크"되는 현상이 발생한다. 위치별로 안정적인
  // 고유 키를 보장하기 위해 충돌 시 인덱스 suffix 를 붙인다.
  const rowKeys = useMemo(() => {
    const seen = new Set<string | number>();
    const dupes = new Set<string | number>();
    return rows.map((r, i) => {
      const raw = rowKey(r, i);
      if (!seen.has(raw)) {
        seen.add(raw);
        return raw;
      }
      dupes.add(raw);
      const deduped = `${raw}__${i}`;
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          `[DataTable] 중복된 rowKey 감지: "${raw}". 위치 ${i} 에 임시 키 "${deduped}" 적용. ` +
            'rowKey 가 고유 값을 반환하도록 데이터/매핑을 점검하세요.',
        );
      }
      return deduped;
    });
  }, [rows, rowKey]);
  const allSelected = rowKeys.length > 0 && rowKeys.every((k) => selectedKeys?.has(k));

  const toggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? new Set() : new Set(rowKeys));
  }, [allSelected, onSelectionChange, rowKeys]);

  const toggleRow = useCallback(
    (key: string | number) => {
      if (!onSelectionChange || !selectedKeys) return;
      const next = new Set(selectedKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      onSelectionChange(next);
    },
    [onSelectionChange, selectedKeys],
  );

  const toggleExpand = useCallback((key: string | number) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const isMobile = useViewportBelow(991);
  const renderAsCards = mobileCardCollapse && isMobile;

  if (renderAsCards) {
    return (
      <div
        className={cn('flex flex-col gap-2 p-2', className)}
        style={{
          border: '1px solid var(--divider-strong)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--surface-raised)',
        }}
        role="list"
        aria-label={ariaLabel}
      >
        {rows.length === 0 ? (
          <div
            className="text-center"
            style={{
              padding: '32px 12px',
              color: 'var(--content-muted)',
              fontSize: 13,
            }}
          >
            {emptyState ?? '데이터가 없습니다.'}
          </div>
        ) : (
          rows.map((row, i) => {
            const k = rowKeys[i] ?? i;
            const isSelected = selectedKeys?.has(k) ?? false;
            return (
              <div
                key={k}
                role="listitem"
                className="flex flex-col gap-1 rounded-md p-3"
                style={{
                  background: isSelected ? 'var(--badge-info-bg)' : 'var(--surface-0)',
                  border: '1px solid var(--divider-soft)',
                }}
              >
                {columns.map((col) => (
                  <div key={col.key} className="flex items-baseline justify-between gap-3">
                    <span
                      className="text-[11px] uppercase tracking-wide"
                      style={{ color: 'var(--content-muted)' }}
                    >
                      {col.header}
                    </span>
                    <span style={{ textAlign: col.align ?? 'right' }}>{col.cell(row, i)}</span>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    );
  }

  return (
    <div
      className={cn('overflow-auto', className)}
      style={{
        border: '1px solid var(--divider-strong)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
        boxShadow: 'var(--shadow-card-rest)',
      }}
    >
      <table className="w-full border-collapse" style={{ tableLayout: 'auto' }} aria-label={ariaLabel} role="table">
        <thead
          style={
            stickyHeader
              ? {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  background: 'var(--surface-1)',
                }
              : undefined
          }
        >
          <tr>
            {selectable ? (
              <th scope="col" style={{ ...thStyle, width: 40 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="전체 선택"
                />
              </th>
            ) : null}
            {expandable ? <th scope="col" style={{ ...thStyle, width: 40 }} aria-hidden="true" /> : null}
            {columns.map((col) => {
              const isSortable = Boolean(col.sortable && onSortChange);
              const isActiveSort = isSortable && sort?.key === col.key;
              const ariaSort: 'ascending' | 'descending' | 'none' | undefined = isSortable
                ? isActiveSort
                  ? sort?.direction === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none'
                : undefined;
              const align = col.headerAlign ?? col.align ?? 'left';
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={ariaSort}
                  style={{
                    ...thStyle,
                    width: col.width,
                    textAlign: align,
                  }}
                >
                  {isSortable ? (
                    <button
                      type="button"
                      onClick={() => onSortChange?.(col.key)}
                      className="inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2"
                      style={{
                        // 헤더 셀의 정렬(중앙/우측)에 버튼도 따라가게 — flex 컨테이너 정렬.
                        justifyContent:
                          align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
                        width: '100%',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        font: 'inherit',
                        color: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      {col.header}
                      <span aria-hidden="true" style={{ fontSize: 11, lineHeight: 1 }}>
                        {isActiveSort ? (sort?.direction === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={(selectable ? 1 : 0) + (expandable ? 1 : 0) + columns.length}
                style={{
                  ...tdStyle,
                  textAlign: 'center',
                  padding: '40px 12px',
                  color: 'var(--content-muted)',
                }}
              >
                {emptyState ?? '데이터가 없습니다.'}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => {
              const k = rowKeys[i] ?? i;
              const isExpanded = expandedKeys.has(k);
              const isSelected = selectedKeys?.has(k) ?? false;
              return (
                <Fragment key={k}>
                  <DataTableRow
                    row={row}
                    index={i}
                    rowKeyValue={k}
                    columns={columns}
                    selectable={Boolean(selectable)}
                    expandable={Boolean(expandable)}
                    isSelected={isSelected}
                    isExpanded={isExpanded}
                    dense={dense}
                    zebra={zebra}
                    tdStyle={tdStyle}
                    onToggleRow={toggleRow}
                    onToggleExpand={toggleExpand}
                  />
                  {expandable && isExpanded && renderExpanded ? (
                    <tr>
                      <td
                        colSpan={(selectable ? 1 : 0) + 1 + columns.length}
                        style={{ ...tdStyle, background: 'var(--surface-1)', padding: 0 }}
                      >
                        {renderExpanded(row)}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
